import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = anon, obj = user
    const [loading, setLoading] = useState(true);
    const lastPingRef = useRef(0);

    // Silently re-validate + refresh token whenever the tab regains focus after
    // being hidden for > 5 minutes. This is the fix for "app is not live after
    // 1-2 hrs of idle" — cold-start-warms the pod AND rolls the JWT forward
    // so users effectively never have to re-login while actively using the app.
    const revalidate = useCallback(async () => {
        const token = localStorage.getItem("micm_token");
        if (!token) return;
        try {
            // First warm the pod (no auth, cheap)
            await api.get("/health").catch(() => {});
            // Then refresh the token
            const { data } = await api.post("/auth/refresh");
            if (data?.token) localStorage.setItem("micm_token", data.token);
            if (data?.id) setUser(data);
        } catch (_e) {
            // Interceptor already handles 401 → /login. Silent otherwise.
        }
    }, []);

    useEffect(() => {
        (async () => {
            const token = localStorage.getItem("micm_token");
            if (!token) {
                setUser(false);
                setLoading(false);
                return;
            }
            // Sep 2026 — "half the Reports menu disappears". This bootstrap
            // used to clear the token on ANY /auth/me failure (`catch (_e)`),
            // so one transient failure — the pod restarting under memory
            // pressure, a dropped request, a 502 — silently signed the user
            // out. AdminLayout gates every role-restricted item on `user`, so
            // most of the Reports menu vanished until a manual reload re-ran
            // this check. Nothing was deleting the menu, and nothing was
            // touching the role permissions.
            //
            // Only an explicit 401/403 means the token is genuinely no longer
            // valid. Everything else is transient: retry briefly and keep the
            // session instead of destroying it.
            const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
            const delays = [0, 600, 1500];
            for (let attempt = 0; attempt < delays.length; attempt++) {
                if (delays[attempt]) await sleep(delays[attempt]);
                try {
                    const { data } = await api.get("/auth/me");
                    setUser(data);
                    setLoading(false);
                    return;
                } catch (e) {
                    const status = e?.response?.status;
                    if (status === 401 || status === 403) {
                        localStorage.removeItem("micm_token");
                        setUser(false);
                        setLoading(false);
                        return;
                    }
                    // Transient — fall through and retry.
                }
            }
            // Still failing after the retries: the backend is unreachable,
            // which says nothing about the token. Keep it so the session
            // survives; the next load or the tab-visibility revalidate above
            // recovers the user without a re-login.
            setUser(false);
            setLoading(false);
        })();
    }, []);

    // Tab-visibility re-auth. Fires when the user comes back to the tab.
    useEffect(() => {
        const onVisible = () => {
            if (document.visibilityState !== "visible") return;
            const now = Date.now();
            // Throttle: only revalidate if we haven't in the last 60 seconds
            if (now - lastPingRef.current < 60_000) return;
            lastPingRef.current = now;
            revalidate();
        };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", onVisible);
        return () => {
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", onVisible);
        };
    }, [revalidate]);

    // Keep-alive ping — every 4 minutes while the tab is visible + the user is
    // logged in, hit /api/health so the (preview) pod doesn't sleep during an
    // active session. Prevents the "data goes zero + please re-login" state
    // users saw after 30-45 min of active use on the preview environment.
    useEffect(() => {
        if (!user || user === false) return;
        const ping = () => {
            if (document.visibilityState !== "visible") return;
            api.get("/health").catch(() => {});
        };
        const t = setInterval(ping, 4 * 60_000);
        return () => clearInterval(t);
    }, [user]);

    const login = async (identifier, password) => {
        try {
            const { data } = await api.post("/auth/login", { identifier, password });
            if (data.token) localStorage.setItem("micm_token", data.token);
            setUser(data);
            return { ok: true, user: data };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    };

    const logout = async () => {
        try {
            await api.post("/auth/logout");
        } catch (_e) {}
        localStorage.removeItem("micm_token");
        // v1.25.8 — Clear any tenant impersonation so the next login starts fresh
        localStorage.removeItem("micm_impersonate_company");
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, revalidate }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}

